import { Channel } from '@at/sauron-platform-core';
import { getChannelSingular, getSearchUrlAdvertisingLocation } from '@at/formatter-utils/lib/channel-utils/channel.util';
import { AdvertCapability } from '../__generated__/graphql-types';
import { decodeFinanceOption } from './finance-options.util';
import { saveToLocalStorage, getSortOrder } from './local-storage-search.util';
export const tpfKeyFormatter = (key) => {
    switch (key) {
        case 'style':
            return 'category';
        case 'minYear':
            return 'year-from';
        case 'maxYear':
            return 'year-to';
        default:
            return key;
    }
};
const mapCapabilitiesQuery = (name, value) => {
    return value.map((capabilityName) => {
        switch (capabilityName) {
            case AdvertCapability.GUARANTEED_PART_EX:
                return 'digital-retailing=GPX';
            default:
                return '';
        }
    });
};
export const keyFormatter = (key) => {
    switch (key) {
        case 'distance':
            return 'radius';
        case 'minPrice':
            return 'price-from';
        case 'maxPrice':
            return 'price-to';
        case 'minSuppliedPrice':
            return 'supplied-price-from';
        case 'maxSuppliedPrice':
            return 'supplied-price-to';
        case 'bodyType':
            return 'body-type';
        case 'minYear':
            return 'year-from';
        case 'maxYear':
            return 'year-to';
        case 'minMonthlyPrice':
            return 'min-monthly-price';
        case 'maxMonthlyPrice':
            return 'max-monthly-price';
        case 'maxMileage':
            return 'maximum-mileage';
        case 'fuelType':
            return 'fuel-type';
        case 'minEngineSize':
        case 'minEngineSizeLitres':
            return 'minimum-badge-engine-size';
        case 'maxEngineSize':
        case 'maxEngineSizeLitres':
            return 'maximum-badge-engine-size';
        case 'sellerType':
            return 'seller-type';
        case 'minSeats':
            return 'minimum-seats';
        case 'maxSeats':
            return 'maximum-seats';
        case 'accelerationValue':
            return 'zero-to-60';
        case 'annualTaxValue':
            return 'annual-tax-cars';
        case 'fuelConsumptionValue':
            return 'fuel-consumption';
        case 'maxInsuranceGroup':
            return 'insuranceGroup';
        case 'co2EmissionValue':
            return 'co2-emissions-cars';
        case 'latLong':
            return 'lat-long';
        case 'doors':
        case 'doorsValue':
            return 'quantity-of-doors';
        case 'minEngineSizeCC':
            return 'cc-from';
        case 'maxEngineSizeCC':
            return 'cc-to';
        case 'batteryRangeValue':
            return 'battery-range';
        case 'batteryChargeTimeValue':
            return 'battery-time';
        case 'batteryQuickChargeTimeValue':
            return 'battery-quick-time';
        case 'partExAvailable':
            return 'part-ex-available';
        case 'endLayout':
            return 'end-layout';
        case 'driverPosition':
            return 'driver-position';
        case 'minExteriorLength':
            return 'min-exterior-length';
        case 'maxExteriorLength':
            return 'max-exterior-length';
        case 'isSellerDefinedUsed':
            return 'is-seller-defined-used';
        case 'bedroomLayout':
            return 'bedroom-layout';
        case 'minMTPLM':
            return 'min-mtplm';
        case 'maxMTPLM':
            return 'max-mtplm';
        case 'trailerAxleNumber':
            return 'trailer_axle_number';
        case 'minEnginePower':
            return 'min-engine-power';
        case 'maxEnginePower':
            return 'max-engine-power';
        case 'withDigitalRetailing':
            return 'with_digital_retailing';
        case 'cabType':
            return 'cab-type';
        default:
            return key;
    }
};
export const mapFinanceOptionQuery = (value) => {
    const financeOption = decodeFinanceOption(value);
    return `deposit=${financeOption.deposit}&term=${financeOption.term}&yearly-mileage=${financeOption.mileage}`;
};
export const mapKeysToQuery = (name, value) => {
    switch (name) {
        case 'deals':
            switch (value.toString()) {
                case 'true':
                    return 'newCarHasDeal=on';
            }
            break;
        case 'showWriteOff':
            switch (value.toString()) {
                case 'false':
                    return 'exclude-writeoff-categories=on';
                case 'true':
                    return 'only-writeoff-categories=on';
            }
            break;
        case 'financeOption':
            return mapFinanceOptionQuery(value);
        case 'isManufacturerApproved':
            return 'ma=Y';
        case 'emissionScheme':
            return 'ulez-compliant=on';
        case 'postalRegion':
            return 'ni-only=on';
        case 'advertClassification':
            return value.includes('non_classified') ? 'include-non-classified=on' : '';
        case 'capabilities':
            return mapCapabilitiesQuery(name, value);
        case 'is-seller-defined-used':
            return `${name}=${encodeURIComponent(value)}`;
        default:
            return `${name}=${encodeURIComponent((value || ''))}`;
    }
};
export const constructSearchQuery = (channel, advertQuery) => {
    const isTpf = [Channel.farm, Channel.plant, Channel.trucks].includes(channel);
    const getAdvertisingLocation = `advertising-location=${getSearchUrlAdvertisingLocation(channel)}`;
    const params = Object.entries(advertQuery)
        .filter(([key]) => key !== 'condition' && key !== 'advertisingLocations')
        .filter(([, value]) => {
        return value !== null && value !== undefined && value !== '';
    })
        .map(([key, value]) => {
        const name = isTpf ? tpfKeyFormatter(key) : keyFormatter(key);
        return mapKeysToQuery(name, value);
    })
        .filter((param) => param !== '' && param !== undefined);
    const queryParams = params.join('&');
    return `${queryParams}&${getAdvertisingLocation}`;
};
const getSearchBaseUrl = (channel) => {
    switch (channel) {
        case Channel.cycles:
            return '/electric-bike-search?';
        default:
            return `/${getChannelSingular(channel)}-search?`;
    }
};
export const getSearchUrl = (channel, advertQuery, searchUrlConfig) => {
    const sortOrder = getSortOrder(channel);
    const sortParam = sortOrder ? `&sort=${sortOrder}` : '';
    const baseSearchUrl = searchUrlConfig[channel] ? `${searchUrlConfig[channel]}?` : getSearchBaseUrl(channel);
    return `${baseSearchUrl}${constructSearchQuery(channel, advertQuery)}&page=1${sortParam}`;
};
export const submitForm = (e, channel, advertQuery) => {
    e.preventDefault();
    if (advertQuery.postcode && advertQuery.latLong) {
        advertQuery.latLong = null;
    }
    saveToLocalStorage(channel, advertQuery);
    window.location.href = getSearchUrl(channel, advertQuery, {});
};
